import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Paper,
  Avatar,
  Grid,
} from "@mui/material";
import { blogService, analyticsService } from "../api/services";
import BlogContent from "../components/Blog/BlogContent";
import AdBanner from "../components/Ads/AdBanner";
import { useAuth } from "../hooks/useAuth";
import AdSense from "../components/AdSense"; // Importar AdSense
import CommentSection from "../components/Blog/CommentSection";

const BlogPost = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);

  const [isPolling, setIsPolling] = useState(false);
  const [showGenerationMessage, setShowGenerationMessage] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await blogService.getPostBySlug(slug);
        const fetchedPost = res.data;
        setPost(fetchedPost);
        setImages(fetchedPost.images || []); 
        
        // Lógica de Polling Inteligente
        // Si es autogenerado, reciente (menos de 10 min) y NO tiene imágenes en el contenido HTML
        if (fetchedPost.is_auto_generated) {
            const hasImagesInContent = fetchedPost.content.includes('<img') || fetchedPost.content.includes('<figure');
            const createdTime = new Date(fetchedPost.created_at || fetchedPost.published_at).getTime();
            const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
            
            if (!hasImagesInContent && createdTime > tenMinutesAgo) {
                console.log("🔄 Iniciando polling de imágenes...");
                setIsPolling(true);
                setShowGenerationMessage(true);
            }
        }
        
      } catch (err) {
        setError(err.response?.data?.message || "Error al cargar el artículo");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Efecto para registrar la visita
  useEffect(() => {
    if (slug) {
      analyticsService.trackView(slug).catch(err => console.error("Error tracking view:", err));
    }
  }, [slug]);

  // Efecto de Polling
  useEffect(() => {
      let intervalId;
      
      if (isPolling && slug) {
          intervalId = setInterval(async () => {
              try {
                  // Agregar timestamp para evitar caché
                  const res = await blogService.getPostBySlug(slug, { params: { t: Date.now() } });
                  const updatedPost = res.data;
                  
                  const hasImages = updatedPost.content.includes('<img') || updatedPost.content.includes('<figure');
                  
                  if (hasImages) {
                      console.log("✅ Imágenes detectadas! Actualizando vista.");
                      setPost(updatedPost);
                      setImages(updatedPost.images || []);
                      setIsPolling(false);
                      setShowGenerationMessage(false);
                  }
              } catch (e) {
                  console.error("Error polling post:", e);
              }
          }, 5000);
      }
      
      return () => {
          if (intervalId) clearInterval(intervalId);
      }
  }, [isPolling, slug]);

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 10 }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  if (!post) return <Alert severity="warning">Artículo no encontrado</Alert>;

  const canEdit =
    user && (user.profile?.role === "admin" || user.profile?.role === "editor");

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Notificación de generación */}
      {showGenerationMessage && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
              Estamos generando las imágenes para este artículo con IA. Aparecerán automáticamente en breve...
          </Alert>
      )}

      {canEdit && (
        <Button
          variant="outlined"
          href={`/admin/posts?edit=${post.id}`}
          sx={{ mb: 2 }}
        >
          ✏️ Editar Post
        </Button>
      )}

      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          {post.title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            src={post.author?.user?.profile_picture}
            alt={post.author?.user?.first_name}
          />
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {post.author?.user?.first_name} {post.author?.user?.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Publicado el{" "}
              {new Date(post.published_at).toLocaleDateString("es-ES")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {post.categories?.map((cat) => (
            <Chip
              key={cat.id}
              label={cat.name}
              sx={{ backgroundColor: cat.color || "#8A2BE2", color: "white" }}
            />
          ))}
        </Box>
      </Paper>

      {/* AdSense - Top placement */}
      <Box sx={{ my: 3 }}>
        <AdSense
          client="ca-pub-XXXXXXXXXXXXXXXX"
          slot="1234567890"
          format="auto"
          responsive="true"
        />
      </Box>

      {/* Contenido del blog */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <BlogContent content={post.content} images={post.images || []} />
      </Paper>

      {/* AdSense - Middle placement */}
      <Box sx={{ my: 4 }}>
        <AdSense
          client="ca-pub-XXXXXXXXXXXXXXXX"
          slot="0987654321"
          format="auto"
          responsive="true"
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* AdSense - Bottom placement */}
      <Box sx={{ my: 3 }}>
        <AdSense
          client="ca-pub-XXXXXXXXXXXXXXXX"
          slot="1122334455"
          format="auto"
          responsive="true"
        />
      </Box>

      {/* Sugerencias */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>
          ¿Te gustó este artículo?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compártelo en redes sociales o deja un comentario.
        </Typography>
      </Box>

      {/* 💬 Sección de Comentarios */}
      {post && <CommentSection postId={post.id} />}

      {/* Ad after comments */}
      <Box sx={{ my: 4 }}>
        <AdBanner format="horizontal" placement="blog_post_after_comments" />
      </Box>

      {/* 📢 Banner de Anuncios Inferior */}
      <Box my={4}>
        <AdBanner placement="footer" />
      </Box>
    </Container>
  );
};

export default BlogPost;
